import { canShowView } from "components/util/views";

// The platform's sections. The project's app is not one of them — it is what the "main"
// section renders — so it never competes to be the default section.
const getAvailableViews = (flow, isDeveloper, hasPermission) => {
  return (flow?.presentationSettings?.views ?? [])
    .filter((view) => view.is_prebuilt)
    .filter((view) => !view.is_hidden)
    .filter((view) => canShowView(view, isDeveloper, hasPermission));
}

const normalizeRedirectKey = (value = "") => {
  return String(value)
    .trim()
    .replace(/^custom-/, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-+/g, "-");
}

const getNumericRedirectId = (value) => {

  if (value == null)
    return null;

  const parsedValue = String(value).trim();
  if (!/^\d+$/.test(parsedValue))
    return null;

  return parseInt(parsedValue);

}

const getStudioRedirectTarget = (currentView = {}) => {

  if (currentView?.redirectType != "studio" || !currentView?.redirectTarget) {
    return {
      nodeId: null,
      pageKey: "",
    };
  }

  const redirectTarget = String(currentView.redirectTarget).trim();
  const redirectNodeId = getNumericRedirectId(redirectTarget);

  if (redirectNodeId != null) {
    return {
      nodeId: redirectNodeId,
      pageKey: "",
    };
  }

  return {
    nodeId: null,
    pageKey: normalizeRedirectKey(redirectTarget),
  };

}

const resolveRedirectSection = (redirect, views = []) => {

  if (!redirect)
    return null;

  const [rawType = "", ...rawTargetParts] = String(redirect).split(",");
  const redirectType = rawType.trim().toLowerCase();
  const redirectTarget = rawTargetParts.join(",").trim();

  if (redirectType === "collections")
    return {
      name: "data",
      redirectType,
      redirectTarget,
    };

  if (redirectType === "studio")
    return {
      name: "processes",
      redirectType,
      redirectTarget,
    };

  if (redirectType !== "view")
    return null;

  // Every project screen lives inside the app, so a view redirect opens "main". Landing on
  // a specific screen of it is a path under /app/<token>, which the app's router reads
  // off the URL itself.
  if (!normalizeRedirectKey(redirectTarget))
    return null;

  return {
    name: "main",
    redirectType,
    redirectTarget,
  };

}

const getDefaultSection = ({ flow, buildStatus, isDeveloper, hasPermission, redirect }) => {

  const views = getAvailableViews(flow, isDeveloper, hasPermission);

  // "agent" and "history" are sections without a view of their own, so only a remembered
  // view is checked: a role can lose a section between two visits.
  const activeSessionView = sessionStorage.getItem(`active_section_${flow.id}`);
  const isSessionViewBlocked = (flow.presentationSettings?.views ?? [])
    .some((view) => view.name == activeSessionView && !views.includes(view));
  if (activeSessionView && !isSessionViewBlocked)
    return { name: activeSessionView };

  if ([
    "PROPOSAL_PROPOSED",
    "PROPOSAL_ADJUSTING",
    "PROTOTYPE_PROPOSED",
    "PROTOTYPE_ADJUSTING",
  ].includes(buildStatus))
    return { name: "agent" };

  const redirectSection = resolveRedirectSection(redirect, views);

  if (redirectSection)
    return redirectSection;

  // A pinned "agent" view takes over "main" as the landing section. A role that can open
  // no section at all still lands on "main": the project's app is all there is to show.
  const defaultView =
    views.find((view) => view.name == "agent" && view.is_pinned) ||
    views.find((view) => view.name == "main") ||
    views[0] ||
    { name: "main" };

  return { name: defaultView.name };

}

export {
  getAvailableViews,
  getDefaultSection,
  getNumericRedirectId,
  getStudioRedirectTarget,
  normalizeRedirectKey,
  resolveRedirectSection,
}
